/**
 * Hook for managing the conversations list.
 * Fetches contacts and their latest messages from Supabase.
 * Subscribes to realtime `messages` and `contacts` for the team; 60s poll as fallback.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { supabase } from "@/lib/data"
import { fetchAllRows } from "../utils/fetchAllRows"
import { useTeamStore } from "@/lib/hooks/useTeam"
import { getCurrentUser } from "@/lib/hooks/useUser"
import {
  ModerationContact,
  ModerationMessage,
  Conversation,
  ConversationFilter,
  ConversationSourceFilter,
  UseConversationsResult,
  DetectedItem,
  EvalItemMetaMap,
} from "../types"
import {
  ContactFlagInfo,
  emptyFlagInfo,
  accumulateFlagInfo,
  computeFlagInfoFromMessages,
  flagInfoEqual,
  isIncomingMessage,
  isBotMessage,
  getSlaHours,
  getSlaStartTimestamp,
  toUnixSeconds,
  categorizeConversation,
  parsePendingFlag,
} from "../utils/conversationUtils"

export type { ContactFlagInfo }
const CONTACT_SELECT_COLUMNS = "id, name, avatar, type, code, team, data, moderation_data, hitl, created_at, severity, summary, lasteval, evaluation, no_reply_needed, internal_comments"
const DECORSIFY_PROXY = "https://signpost-ia-app-qa.azurewebsites.net/decorsify/"

function readProjectIdFromIntegration(input: unknown): string | undefined {
  if (!input || typeof input !== "object") return undefined
  const raw = input as Record<string, unknown>
  const candidates = [raw.projectId, raw.project_id, raw.telerivetProjectId, raw.telerivet_project_id]
  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) return value
  }
  return undefined
}

async function parseJsonSafe(response: Response): Promise<unknown> {
  const raw = await response.text()
  if (!raw || !raw.trim()) return null
  return JSON.parse(raw) as unknown
}

async function fetchTelerivetRouteNameMap(projectId: string, apiKey: string): Promise<Map<string, string>> {
  const endpoint = `https://api.telerivet.com/v1/projects/${projectId}/route_list?api_key=${encodeURIComponent(apiKey)}`
  const loadRouteList = async (url: string) => {
    const resp = await fetch(url, { method: "GET", headers: { "Content-Type": "application/json" } })
    if (!resp.ok) throw new Error(`Failed to fetch route list (${resp.status})`)
    const parsed = await parseJsonSafe(resp)
    return Array.isArray(parsed) ? parsed : []
  }

  let rows: unknown[] = []
  rows = await loadRouteList(`${DECORSIFY_PROXY}${endpoint}`)

  const map = new Map<string, string>()
  for (const row of rows) {
    if (!row || typeof row !== "object") continue
    const asObj = row as Record<string, unknown>
    const id = typeof asObj.id === "string" ? asObj.id : undefined
    const name = typeof asObj.name === "string" && asObj.name.trim() ? asObj.name : id
    if (id && name) map.set(id, name)
  }
  return map
}

/**
 * Messages backing the conversation list on first load. This is a PREVIEW
 * window, not the history: it fills in last-message text, flag counts and
 * unread badges. 100 covers the recently-active conversations that matter on
 * open, and "Load more" extends it.
 */
const MESSAGE_WINDOW_INITIAL = 100

/** How much each "Load more" adds. */
const MESSAGE_WINDOW_STEP = 200

/** Cap on server-side search matches, to bound one query's cost. */
const SEARCH_MATCH_LIMIT = 500

export function useConversations(): UseConversationsResult {
  const [allContacts, setAllContacts] = useState<ModerationContact[]>([])
  const [lastMessagesByContact, setLastMessagesByContact] = useState<Map<string, ModerationMessage>>(new Map())
  const [messageSearchTextByContact, setMessageSearchTextByContact] = useState<Map<string, string>>(new Map())
  const [flagInfoByContact, setFlagInfoByContact] = useState<Map<string, ContactFlagInfo>>(new Map())
  const [unreadCountByContact, setUnreadCountByContact] = useState<Map<string, number>>(new Map())
  const [evalItemMap, setEvalItemMap] = useState<EvalItemMetaMap>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<ConversationFilter>("all")
  const [sourceFilter, setSourceFilter] = useState<ConversationSourceFilter>("real_only")
  const [searchQuery, setSearchQuery] = useState("")
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)

  /*
   * How many recent messages back the conversation list. Starts small and
   * grows on demand rather than loading a whole team's history up front.
   * Search is unaffected -- it queries the server (see searchMatchIds).
   */
  const [messageWindow, setMessageWindow] = useState(MESSAGE_WINDOW_INITIAL)
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false)

  /** Contacts whose message text matches the query, resolved server-side. */
  const [searchMatchIds, setSearchMatchIds] = useState<Set<string> | null>(null)
  const [searching, setSearching] = useState(false)

  const hasFetchedRef = useRef(false)
  const evalItemMapRef = useRef<EvalItemMetaMap>({})
  const allContactsRef = useRef<ModerationContact[]>([])
  const { selectedTeam } = useTeamStore()

  const parseAssignedUserId = (moderationData: ModerationContact["moderation_data"]): string | null => {
    if (typeof moderationData !== "string" || !moderationData.trim()) return null
    try {
      const parsed = JSON.parse(moderationData) as { assigned_to_id?: unknown }
      return typeof parsed.assigned_to_id === "string" && parsed.assigned_to_id.trim()
        ? parsed.assigned_to_id
        : null
    } catch {
      return null
    }
  }

  // Same precedence ContactList uses: an explicit handled_by in moderation_data
  // wins over the raw hitl column, which only flips once a human sends a
  // message and can lag the moderator's actual mode toggle.
  const parseHandledBy = (contact: ModerationContact): "human" | "bot" => {
    if (typeof contact.moderation_data === "string" && contact.moderation_data.trim()) {
      try {
        const parsed = JSON.parse(contact.moderation_data) as { handled_by?: unknown }
        if (parsed.handled_by === "human" || parsed.handled_by === "bot") return parsed.handled_by
      } catch {
        // fall through to hitl
      }
    }
    return contact.hitl ? "human" : "bot"
  }

  const parseLastReadUnix = (moderationData: ModerationContact["moderation_data"]): number => {
    if (typeof moderationData !== "string" || !moderationData.trim()) return 0
    try {
      const parsed = JSON.parse(moderationData) as { last_read_message_created_at?: unknown }
      const raw = parsed.last_read_message_created_at
      if (typeof raw !== "string" || !raw.trim()) return 0
      const ms = new Date(raw).getTime()
      if (!Number.isFinite(ms)) return 0
      return Math.floor(ms / 1000)
    } catch {
      return 0
    }
  }

  useEffect(() => {
    getCurrentUser().then(({ data }) => { if (data) setCurrentUserId(data.id) })
  }, [])

  const fetchAllData = useCallback(async () => {
    if (!selectedTeam?.id) return
    if (!hasFetchedRef.current) setLoading(true)
    setError(null)

    try {
      // 0. Fetch eval items for name lookup (cached after first load)
      if (Object.keys(evalItemMapRef.current).length === 0) {
        const { data: evalRows } = await supabase.from("eval_items").select("id, name, highrisk, lowconf, askhuman")
        if (evalRows) {
          const map: EvalItemMetaMap = {}
          evalRows.forEach(item => {
            map[item.id as number] = {
              name: item.name ?? "",
              highrisk: item.highrisk,
              lowconf: item.lowconf,
              askhuman: item.askhuman,
            }
          })
          evalItemMapRef.current = map
          setEvalItemMap(map)
        }
      }
      const currentEvalItemMap = evalItemMapRef.current

      // 1. Fetch all user+synthetic contacts for this team.
      // Paginated: without a range this is capped at PostgREST's max-rows
      // (1000), silently, so teams past that lost conversations outright.
      const { rows: contactRows } = await fetchAllRows<any>((from, to) =>
        supabase
          .from("contacts")
          .select(CONTACT_SELECT_COLUMNS)
          .eq("team", selectedTeam.id)
          .in("type", ["user", "synthetic"])
          .order("created_at", { ascending: false })
          .range(from, to))

      const contacts: ModerationContact[] = (contactRows || []).map((row) => {
        const r = row as any
        return {
          ...r,
          evaluation: r.evaluation as any,
          internal_comments: r.internal_comments as any,
        }
      })

      const contactFingerprint = (c: ModerationContact) =>
        `${c.id}|${c.no_reply_needed ?? ""}|${c.severity ?? ""}|${c.summary ?? ""}|${c.lasteval ?? ""}|${c.name ?? ""}|${(c.moderation_data as string) ?? ""}|${c.hitl === true ? "1" : "0"}`

      setAllContacts(prev => {
        if (prev.length === contacts.length) {
          const prevKey = prev.map(contactFingerprint).join(",")
          const newKey  = contacts.map(contactFingerprint).join(",")
          if (prevKey === newKey) return prev
        }
        allContactsRef.current = contacts
        return contacts
      })

      /*
       * 2. Recent messages, for the last-message preview, flag counts and
       * unread badges.
       *
       * Deliberately BOUNDED. Pulling the whole history to populate a list
       * costs more the longer a team has been running, for rows that mostly
       * never render. `messageWindow` grows when the user asks for more.
       *
       * Message-text SEARCH does not depend on this window -- it runs
       * server-side (see searchMatchIds below), so searching reaches the
       * entire history regardless of how much is loaded here.
       */
      const { data: messageRows, error: msgErr } = await supabase
        .from("messages")
        .select("id, contact, role, message, channel, created_at, sender, integration, user_detected_items, agent_detected_items, escalation_detected, escalation_from_level, escalation_to_level, escalation_reasoning, agent_appropriate, agent_concern_level, agent_reasoning, narrative_update, rating, rating_example, custom_message_flags, highrisk, lowconf, askhuman")
        .eq("team", selectedTeam.id)
        .order("created_at", { ascending: false })
        .limit(messageWindow)

      if (msgErr) throw msgErr

      // A full page means older messages exist that we have not pulled.
      setHasMoreMessages((messageRows || []).length >= messageWindow)

      const telerivetProjectIds = new Set<string>()
      for (const row of messageRows || []) {
        const r = row as any
        if (r.channel !== "telerivet") continue
        const projectId = readProjectIdFromIntegration(r.integration)
        if (projectId) telerivetProjectIds.add(projectId)
      }

      const routeNameByRouteId = new Map<string, string>()
      if (telerivetProjectIds.size > 0) {
        try {
          const { data: keyRows } = await supabase
            .from("api_keys")
            .select("type, key")
            .eq("team_id", selectedTeam.id)
          const telerivetApiKey = (keyRows || []).find((row: any) => row.type === "telerivet")?.key
          if (telerivetApiKey) {
            for (const projectId of telerivetProjectIds) {
              const routeMap = await fetchTelerivetRouteNameMap(projectId, telerivetApiKey)
              routeMap.forEach((name, routeId) => routeNameByRouteId.set(routeId, name))
            }
          }
        } catch {
          // best-effort only; fall back to raw channel names if route lookup fails
        }
      }

      const messages: ModerationMessage[] = (messageRows || []).map(r => ({
        ...r,
        rating: (r.rating as "0" | "1" | null) ?? null,
        integration: (() => {
          const base = (r.integration || {}) as Record<string, unknown>
          const routeId = typeof base.route_id === "string" ? base.route_id : undefined
          const routeName = routeId ? routeNameByRouteId.get(routeId) : undefined
          return routeName ? { ...base, route_name: routeName } as any : base as any
        })(),
        user_detected_items: r.user_detected_items as unknown as DetectedItem[] | null,
        agent_detected_items: r.agent_detected_items as unknown as DetectedItem[] | null,
        custom_message_flags: r.custom_message_flags as unknown as FlagDetectedItem[] | null,
      }))

      // 3. Build last-message map and flag map
      const messageMap = new Map<string, ModerationMessage>()
      const messageSearchMap = new Map<string, string>()
      const flagMap = new Map<string, ContactFlagInfo>()
      const unreadMap = new Map<string, number>()
      const lastReadByContact = new Map<string, number>()

      for (const c of contacts) {
        lastReadByContact.set(c.id, parseLastReadUnix(c.moderation_data))
      }

      // Messages come back newest-first; we want oldest-first for flag accumulation
      const reversed = [...messages].reverse()

      for (const msg of reversed) {
        const cid = msg.contact
        if (!cid) continue

        // Track last (most recent) message per contact
        messageMap.set(cid, msg)

        const messageText = typeof msg.message === "string" ? msg.message.trim() : ""
        if (messageText) {
          const current = messageSearchMap.get(cid)
          messageSearchMap.set(cid, current ? `${current}\n${messageText}` : messageText)
        }

        if (!flagMap.has(cid)) flagMap.set(cid, emptyFlagInfo())
        accumulateFlagInfo(flagMap.get(cid)!, msg, currentEvalItemMap)

        if (isIncomingMessage(msg)) {
          const lastReadTs = lastReadByContact.get(cid) || 0
          const msgTs = toUnixSeconds(msg.created_at)
          if (msgTs > lastReadTs) {
            unreadMap.set(cid, (unreadMap.get(cid) || 0) + 1)
          }
        }
      }

      // 4. Finalize flag info per contact: sort eval items, compute SLA from contact.severity, needsReply, isResolved
      const contactMap = new Map(contacts.map(c => [c.id, c]))

      // Build per-contact message list for SLA flaggedAt calculation
      const msgsByContact = new Map<string, ModerationMessage[]>()
      for (const msg of reversed) {
        if (!msg.contact) continue
        if (!msgsByContact.has(msg.contact)) msgsByContact.set(msg.contact, [])
        msgsByContact.get(msg.contact)!.push(msg)
      }

      flagMap.forEach((info, contactId) => {
        const contact = contactMap.get(contactId)
        const lastMessage = messageMap.get(contactId)

        // Sort eval items by maxSeverity desc
        if (info.evalItems) info.evalItems.sort((a, b) => b.maxSeverity - a.maxSeverity)

        // SLA from contact-level severity, only when there are unresolved flags
        info.contactSeverity = contact?.severity ?? null
        const hasUnresolved = info.hasHighRisk || info.hasLowConfidence || info.hasAskedHuman ||
          Object.keys(info.activeFlags || {}).length > 0
        if (hasUnresolved && contact?.severity != null) {
          const slaHours = getSlaHours(String(contact.severity))
          if (slaHours !== null) {
            const contactMsgs = msgsByContact.get(contactId) || []
            const slaStartTs = getSlaStartTimestamp(contactMsgs)
            info.flaggedAt   = slaStartTs
            info.slaDeadline = slaStartTs + slaHours * 3600
          }
          const topItem = (info.evalItems || [])[0]
          info.riskCategoryLevel = String(contact.severity)
          info.riskCategoryName  = topItem?.name || `Severity ${contact.severity}`
        }

        if (lastMessage) {
          const noReplyNeeded = contact?.no_reply_needed === true
          info.needsReply = isIncomingMessage(lastMessage) && !noReplyNeeded
        }
        info.isResolved = info.hasAnyResolvedFlag && !info.hasHighRisk && !info.hasLowConfidence && !info.hasAskedHuman &&
          Object.keys(info.activeFlags || {}).length === 0
      })

      setLastMessagesByContact(prev => {
        if (prev.size === messageMap.size) {
          let same = true
          messageMap.forEach((msg, cid) => {
            const old = prev.get(cid)
            if (!old || old.id !== msg.id || old.created_at !== msg.created_at) same = false
          })
          if (same) return prev
        }
        return messageMap
      })

      setMessageSearchTextByContact(prev => {
        if (prev.size === messageSearchMap.size) {
          let same = true
          messageSearchMap.forEach((text, cid) => {
            if ((prev.get(cid) || "") !== text) same = false
          })
          if (same) return prev
        }
        return messageSearchMap
      })

      setFlagInfoByContact(prev => {
        if (prev.size === flagMap.size) {
          let same = true
          flagMap.forEach((info, cid) => {
            const old = prev.get(cid)
            if (!old || !flagInfoEqual(old, info)) same = false
          })
          if (same) return prev
        }
        return flagMap
      })

      setUnreadCountByContact(prev => {
        if (prev.size === unreadMap.size) {
          let same = true
          unreadMap.forEach((count, cid) => {
            if ((prev.get(cid) || 0) !== count) same = false
          })
          if (same) return prev
        }
        return unreadMap
      })

      hasFetchedRef.current = true
    } catch (err) {
      console.error("[useConversations] fetch failed:", err)
      if (!hasFetchedRef.current) setError(err instanceof Error ? err.message : "Failed to fetch conversations")
    } finally {
      setLoading(false)
      setLoadingMoreMessages(false)
    }
  }, [selectedTeam?.id, messageWindow])

  // Initial fetch, and refetch when the window grows.
  useEffect(() => {
    if (selectedTeam?.id) fetchAllData()
  }, [selectedTeam?.id, fetchAllData])

  /** Pull an older slice of message history into the list. */
  const loadMoreMessages = useCallback(() => {
    setLoadingMoreMessages(true)
    setMessageWindow(w => w + MESSAGE_WINDOW_STEP)
  }, [])

  /*
   * Message-text search, server-side.
   *
   * The rest of the filtering runs against contacts already in memory, but
   * message bodies cannot: only a slice of them is loaded. Searching that
   * slice would quietly limit results to recent conversations, which is worse
   * than not searching bodies at all -- the user cannot tell an absent match
   * from an unloaded one. So the query goes to the database, which sees
   * everything.
   *
   * Name / id / custom-field matching stays client-side: those live on the
   * contact rows, which are all loaded.
   */
  useEffect(() => {
    const q = searchQuery.trim()
    if (!selectedTeam?.id || q.length < 2) {
      setSearchMatchIds(null)
      setSearching(false)
      return
    }

    let cancelled = false
    setSearching(true)
    // Debounced: this fires per keystroke otherwise.
    const timer = setTimeout(async () => {
      try {
        // Escape PostgREST pattern metacharacters so a literal % or _ in the
        // query does not silently become a wildcard.
        const escaped = q.replace(/[%_\\]/g, ch => `\\${ch}`)
        const { data, error } = await supabase
          .from("messages")
          .select("contact")
          .eq("team", selectedTeam.id)
          .ilike("message", `%${escaped}%`)
          .order("created_at", { ascending: false })
          .limit(SEARCH_MATCH_LIMIT)
        if (cancelled) return
        if (error) throw error
        setSearchMatchIds(new Set((data || []).map((r: any) => r.contact).filter(Boolean)))
      } catch (err) {
        if (cancelled) return
        console.error("[useConversations] message search failed:", err)
        // Fall back to whatever is loaded rather than showing nothing.
        setSearchMatchIds(null)
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 250)

    return () => { cancelled = true; clearTimeout(timer) }
  }, [searchQuery, selectedTeam?.id])

  // Realtime: conversation list + previews when any team message changes
  useEffect(() => {
    if (!selectedTeam?.id) return

    let debounceTimer: ReturnType<typeof setTimeout> | undefined
    const scheduleRefetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        void fetchAllData()
      }, 250)
    }

    const channel = supabase
      .channel(`moderation-messages:${selectedTeam.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "messages",
          filter: `team=eq.${selectedTeam.id}`,
        },
        scheduleRefetch
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "contacts",
          filter: `team=eq.${selectedTeam.id}`,
        },
        scheduleRefetch
      )
      .subscribe()

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      void supabase.removeChannel(channel)
    }
  }, [selectedTeam?.id, fetchAllData])

  // Fallback polling if realtime is unavailable (only after initial load)
  useEffect(() => {
    if (!selectedTeam?.id) return
    const interval = setInterval(() => {
      if (hasFetchedRef.current) void fetchAllData()
    }, 60000)
    return () => clearInterval(interval)
  }, [selectedTeam?.id, fetchAllData])

  const updateContactLocally = useCallback((contactId: string, updates: Partial<ModerationContact>) => {
    setAllContacts(prev => {
      const next = prev.map(c => c.id === contactId ? { ...c, ...updates } : c)
      allContactsRef.current = next
      return next
    })
    if (typeof updates.moderation_data === "string" && updates.moderation_data.includes("last_read_message")) {
      setUnreadCountByContact(prev => {
        const next = new Map(prev)
        next.set(contactId, 0)
        return next
      })
    }
  }, [])

  const updateContactFlags = useCallback((contactId: string, messages: ModerationMessage[]) => {
    const contact = allContactsRef.current.find(c => c.id === contactId)
    const flagInfo = computeFlagInfoFromMessages(messages, contact || { id: contactId }, evalItemMapRef.current)
    setFlagInfoByContact(prev => {
      const existing = prev.get(contactId)
      if (existing && flagInfoEqual(existing, flagInfo)) return prev
      const newMap = new Map(prev)
      newMap.set(contactId, flagInfo)
      return newMap
    })
  }, [])

  const filteredConversations = useMemo((): Conversation[] => {
    let filtered = allContacts

    if (sourceFilter === "real_only") {
      filtered = filtered.filter(c => c.type !== "synthetic")
    } else if (sourceFilter === "synthetic_only") {
      filtered = filtered.filter(c => c.type === "synthetic")
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      filtered = filtered.filter(c => {
        const nameMatches = c.name?.toLowerCase().includes(q)
        const idMatches = c.id?.toLowerCase().includes(q)

        let customMatches = false
        const customIdKeys = [
          "custom_id",
          "customId",
          "contact_id",
          "contactId",
          "external_id",
          "externalId",
          "phone",
          "phone_number",
        ]
        try {
          const raw = c.data
          const parsed = typeof raw === "string" && raw.trim()
            ? JSON.parse(raw)
            : raw
          if (parsed && typeof parsed === "object") {
            customMatches = customIdKeys.some((key) => {
              const value = (parsed as Record<string, unknown>)[key]
              return typeof value === "string" && value.toLowerCase().includes(q)
            })
          }
        } catch {
          customMatches = false
        }

        /*
         * Message-body matches come from the server (searchMatchIds), which
         * sees the whole history. The in-memory text is still checked as a
         * fast path so results appear before the query returns, and as the
         * fallback if it fails.
         */
        const messageMatches =
          (searchMatchIds?.has(c.id) ?? false) ||
          (messageSearchTextByContact.get(c.id) || "").toLowerCase().includes(q)

        return Boolean(nameMatches || idMatches || customMatches || messageMatches)
      })
    }

    switch (filter) {
      case "flagged":
        filtered = filtered.filter(c => {
          const info = flagInfoByContact.get(c.id)
          return info?.hasHighRisk || info?.hasLowConfidence || info?.hasAskedHuman ||
            Object.keys(info?.activeFlags || {}).length > 0
        })
        break
      case "human":
        filtered = filtered.filter(c => c.hitl === true)
        break
      case "bot":
        filtered = filtered.filter(c => c.hitl !== true)
        break
      case "mine":
        // Contacts explicitly assigned to the current auth user.
        if (currentUserId) {
          filtered = filtered.filter(c => {
            const assignedToId = parseAssignedUserId(c.moderation_data)
            return assignedToId === currentUserId
          })
        }
        break
      case "unassigned":
        filtered = filtered.filter(c => {
          const info = flagInfoByContact.get(c.id)
          const lastMsg = lastMessagesByContact.get(c.id)
          const hasAnyActive = info?.hasHighRisk || info?.hasLowConfidence || info?.hasAskedHuman ||
            Object.keys(info?.activeFlags || {}).length > 0
          return hasAnyActive && !lastMsg?.sender
        })
        break
      case "resolved":
        // Delegates to the same categorizer ContactList's "Resolved" section
        // uses, so this filter and that section can never disagree about
        // which contacts belong here.
        filtered = filtered.filter(c => categorizeConversation({
          flags: flagInfoByContact.get(c.id),
          handledBy: parseHandledBy(c),
          pending: parsePendingFlag(c.moderation_data),
        }) === "resolved")
        break
      case "open":
        filtered = filtered.filter(c => categorizeConversation({
          flags: flagInfoByContact.get(c.id),
          handledBy: parseHandledBy(c),
          pending: parsePendingFlag(c.moderation_data),
        }) === "open")
        break
      case "pending":
        filtered = filtered.filter(c => categorizeConversation({
          flags: flagInfoByContact.get(c.id),
          handledBy: parseHandledBy(c),
          pending: parsePendingFlag(c.moderation_data),
        }) === "pending")
        break
    }

    return filtered.map(contact => ({
      contact,
      lastMessage: lastMessagesByContact.get(contact.id),
      unreadCount: unreadCountByContact.get(contact.id) || 0,
      flags: flagInfoByContact.get(contact.id),
    }))
  }, [allContacts, lastMessagesByContact, messageSearchTextByContact, flagInfoByContact, unreadCountByContact, filter, sourceFilter, searchQuery, searchMatchIds, currentUserId])

  return {
    conversations: filteredConversations,
    loading,
    error,
    filter,
    sourceFilter,
    searchQuery,
    setFilter,
    setSourceFilter,
    setSearchQuery,
    refresh: fetchAllData,
    updateContactLocally,
    updateContactFlags,
    // Message-window paging for the list.
    hasMoreMessages,
    loadingMoreMessages,
    loadMoreMessages,
    /** True while a server-side message search is in flight. */
    searching,
  }
}
